import { ChevronUp, Clock, PhoneIncoming02, PhoneOutgoing02, User01 } from '@untitled-ui/icons-react/build/cjs';
import moment from 'moment';
import React, { useRef, useState } from 'react';
import CheckCircleDark from 'src/assets/img/icons/filled-icons/check-circle-dark.png';
import inboundEmailIcon from 'src/assets/img/icons/mail-arrow-down-dark-gray.svg';
import outboundEmailIcon from 'src/assets/img/icons/mail-arrow-up-dark-gray.svg';
import verifiedIcon from 'src/assets/img/verified-tick.svg';
import verifiedGrayIcon from 'src/assets/img/Verifiedtickgray.svg';
import ButtonCmp from 'src/components/ButtonCmp';
import InputText from 'src/components/InputText/InputText';
import LiveTimer from 'src/components/LiveTimer';
import OutsideClickHandler from 'src/components/OutsideClickHandler';
import SelectBox from 'src/components/SelectBox/SelectBox';
import TooltipCmp from 'src/components/TooltipCmp';
import { getFormattedNumber } from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';

// Dual-currency labels, ported verbatim from the recap generator
// (wal-backend-v2/api/helpers/agent-space/check-and-emit-rate-recap.js → formatRateLabel / formatAggregateLabel)
// so the footer tooltips read identically to the "Here's the recap …" paragraph.
const formatMoney = (n: number) => `$${getFormattedNumber(Math.round(n), false, false, true)}`;

// Single rate → "$1,268 CAD ($894 USD)", or just "$1,268 CAD" when USD / no USD value / the two round equal.
const formatRateLabelDual = (item?: { amount?: number; amountUSD?: number; currency?: string }): string => {
    if (!item || item.amount == null) return '—';
    const original = `${formatMoney(item.amount)}${item.currency ? ` ${item.currency}` : ''}`;

    if (item.amountUSD == null || item.currency === 'USD' || Math.round(item.amount) === Math.round(item.amountUSD)) {
        return original;
    }

    return `${original} (${formatMoney(item.amountUSD)} USD)`;
};

// Aggregate → "$2,735 CAD ($1,930 USD)". Collapses to "$1,930 USD" ONLY when there's
// no conversion to show (every rate already USD → original rounds to the same value).
// Whenever a CAD rate is in the mix the two values differ, so we always surface the
// dual label; the primary currency defaults to CAD (WAL is CAD-primary) when the
// dominant isn't a non-USD currency, so the CAD figure never goes missing.
const formatAggregateLabelDual = (originalValue: number, usdValue: number, dominantCurrency?: string): string => {
    if (!Number.isFinite(originalValue) || Math.round(originalValue) === Math.round(usdValue)) {
        return `${formatMoney(usdValue)} USD`;
    }

    const primaryCurrency = dominantCurrency && dominantCurrency !== 'USD' ? dominantCurrency : 'CAD';

    return `${formatMoney(originalValue)} ${primaryCurrency} (${formatMoney(usdValue)} USD)`;
};

export interface IAllRatesItem {
    label: string;
    icon?: 'verified' | string;
    actionType?: 'inboundCall' | 'outboundCall' | 'outboundEmail' | 'inboundEmail' | 'manual';
    value?: string;
    // Raw numeric rate (currency-agnostic) used to compute the Avg/Min/Max footer; `value` stays the display string.
    amount?: number;
    // Currency of `amount` (e.g. 'CAD' / 'USD'). Drives the per-stat currency in the footer tooltips;
    // undefined when the source didn't report one (the value is still counted in the stats).
    currency?: string;
    // USD-equivalent of `amount` (BE-converted offerAmountUSD). Powers the dual-currency footer tooltips
    // ("$X CAD ($Y USD)"). Undefined for sources that don't carry it; the aggregate falls back to `amount`.
    amountUSD?: number;
    status?: 'completed' | 'loading';
    onItemClick?: () => void;
}

interface IAllRatesPosting {
    id: number;
    refNumber: string;
    isExpired?: boolean;
    isArchived?: boolean;
    /** Call settings row from by-quote postingDetails — window anchor (updatedAt) + target/duration */
    truckFindingAi?: { totalRates?: number | null; acceptInboundCallDuration?: number | null; updatedAt?: string } | null;
}

interface IProps {
    items: IAllRatesItem[];
    postings?: IAllRatesPosting[];
    selectedPostId?: number | null;
    onSelectPostId?: (postId: number | null) => void;
    isOpen?: boolean;
    onToggle?: () => void;
}

const AllRatesLists = ({ items, postings = [], selectedPostId, onSelectPostId, isOpen: controlledIsOpen, onToggle }: IProps) => {
    const [internalIsOpen, setInternalIsOpen] = useState(true);
    const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
    const handleToggle = onToggle || (() => setInternalIsOpen((prev) => !prev));

    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [settingsDropPosition, setSettingsDropPosition] = useState<'top' | 'bottom'>('top');
    const settingsTriggerRef = useRef<HTMLDivElement | null>(null);
    const [targetRatesInput, setTargetRatesInput] = useState('');
    const [acceptCallsForInput, setAcceptCallsForInput] = useState('');
    const [isTargetRatesError, setIsTargetRatesError] = useState(false);
    const [isAcceptCallsForError, setIsAcceptCallsForError] = useState(false);

    // Saved values reflect immediately without waiting for a by-quote refetch.
    const [postingSettingsOverrides, setPostingSettingsOverrides] = useState<Record<number, { targetRates: number; acceptCallsFor: number }>>({});

    const selectedPosting = selectedPostId != null ? postings.find((p) => p.id === selectedPostId) : null;
    const settingsOverride = selectedPostId != null ? postingSettingsOverrides[selectedPostId] : undefined;
    const targetRates = settingsOverride?.targetRates ?? selectedPosting?.truckFindingAi?.totalRates ?? null;
    const acceptCallsFor = settingsOverride?.acceptCallsFor ?? selectedPosting?.truckFindingAi?.acceptInboundCallDuration ?? null;

    // Call window of the selected posting — same basis as the BE expiry check: the anchor is
    // truck_finding_ai.updatedAt (NOT the save moment — update-call-settings preserves it) and the
    // window runs acceptInboundCallDuration minutes from there (see fetch-load-postings).
    const callsStartAt = selectedPosting?.truckFindingAi?.updatedAt && acceptCallsFor ? moment.utc(selectedPosting.truckFindingAi.updatedAt).toISOString() : null;
    const callsEndAt = callsStartAt ? moment.utc(callsStartAt).add(acceptCallsFor, 'minutes').toISOString() : null;

    // NOTE: the source repo persists this via LoadLinkService.updateLoadPostingCallSettings, which
    // hits a LOAD_LINK.UPDATE_CALL_SETTINGS endpoint that doesn't exist in this repo's API constants
    // yet. Kept local-only (optimistic override state) until that BE endpoint/service is ported here.
    const handleSaveCallSettings = async (settings: { targetRates: number; acceptCallsFor: number }) => {
        if (selectedPostId == null) return;

        setPostingSettingsOverrides((prev) => ({ ...prev, [selectedPostId]: settings }));
        WalToast.success('Call settings saved.');
    };

    // Approximate rendered height of the settings dropdown, used to decide open direction.
    const SETTINGS_DROPDOWN_HEIGHT = 230;

    const handleSettingsToggle = () => {
        setIsSettingsOpen((prev) => {
            // Seed inputs from props each time the dropdown opens so stale edits are discarded.
            if (!prev) {
                setTargetRatesInput(targetRates != null ? String(targetRates) : '');
                setAcceptCallsForInput(acceptCallsFor != null ? `${acceptCallsFor} min` : '');
                setIsTargetRatesError(false);
                setIsAcceptCallsForError(false);

                // The widget sits right above the chat input, so bottom space is usually tight:
                // open upward by default and only open downward when the viewport has room below.
                const triggerRect = settingsTriggerRef.current?.getBoundingClientRect();
                setSettingsDropPosition(triggerRect && window.innerHeight - triggerRect.bottom >= SETTINGS_DROPDOWN_HEIGHT ? 'bottom' : 'top');
            }

            return !prev;
        });
    };

    // Minutes already elapsed in the call window — time gone can't be reclaimed, so it's the
    // floor for any new Accept Calls For value (matches the BE validation in update-call-settings).
    const elapsedMinutes = callsStartAt ? Math.max(0, Math.floor(moment.utc().diff(moment.utc(callsStartAt)) / 60000)) : 0;

    const handleSettingsSave = () => {
        const targetRatesValue = targetRatesInput.replace(/\D/g, '');
        const acceptCallsForValue = acceptCallsForInput.replace(/\D/g, '');
        const isAcceptCallsForInvalid = !acceptCallsForValue || Number(acceptCallsForValue) <= elapsedMinutes;
        setIsTargetRatesError(!targetRatesValue);
        setIsAcceptCallsForError(isAcceptCallsForInvalid);

        if (!targetRatesValue || isAcceptCallsForInvalid) return;

        handleSaveCallSettings({ targetRates: Number(targetRatesValue), acceptCallsFor: Number(acceptCallsForValue) });
        setIsSettingsOpen(false);
    };

    const refIdOptions = [{ label: 'All Rates', value: 'all' }, ...postings.map((p) => ({ label: `#${p.refNumber}`, value: String(p.id) }))];
    const refIdValue = selectedPostId == null ? refIdOptions[0] : refIdOptions.find((o) => o.value === String(selectedPostId)) || refIdOptions[0];

    // Count reflects the visible list (items are already filtered by the selected REF ID).
    const completedRates = items.filter((i) => i.status === 'completed').length;

    // Avg/Min/Max summary — completed rates only. Amounts are raw numbers across mixed currencies
    // (CAD/USD), so the figures are NOT currency-converted; the footer tooltips spell out the per-stat
    // currency. NaN from a non-numeric amount fails `> 0`, so it's excluded.
    const completedRateItems = items.filter((i) => i.status === 'completed' && typeof i.amount === 'number' && (i.amount as number) > 0);
    const completedAmounts = completedRateItems.map((i) => i.amount as number);
    const hasRateStats = completedAmounts.length > 0;
    const avgRate = hasRateStats ? completedAmounts.reduce((sum, n) => sum + n, 0) / completedAmounts.length : 0;
    const minRate = hasRateStats ? Math.min(...completedAmounts) : 0;
    const maxRate = hasRateStats ? Math.max(...completedAmounts) : 0;

    // Footer tooltips show the dual-currency value ("$X CAD ($Y USD)"). Min/Max trace back to the specific rate
    // that produced the value (with its own USD equivalent); Avg USD-normalizes every completed rate
    // (offerAmountUSD, falling back to the raw amount when a source didn't carry one — same fallback as the
    // recap) and labels the dominant currency with the converted USD mean in parentheses. Mirrors
    // check-and-emit-rate-recap.js so the tooltip matches the recap paragraph.
    const minRateItem = completedRateItems.find((i) => i.amount === minRate);
    const maxRateItem = completedRateItems.find((i) => i.amount === maxRate);
    const avgRateUSD = hasRateStats ? completedRateItems.reduce((sum, i) => sum + (i.amountUSD ?? (i.amount as number)), 0) / completedRateItems.length : 0;
    const currencyCounts = completedRateItems.reduce<Record<string, number>>((acc, i) => {
        if (i.currency) acc[i.currency] = (acc[i.currency] || 0) + 1;

        return acc;
    }, {});
    const dominantCurrency = Object.entries(currencyCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
    const minRateDisplay = formatRateLabelDual(minRateItem);
    const maxRateDisplay = formatRateLabelDual(maxRateItem);
    const avgRateTooltip = `Average rate: ${formatAggregateLabelDual(avgRate, avgRateUSD, dominantCurrency)}`;

    // Call settings are per-posting, so the counter (completed/target) and the settings dropdown
    // only exist when a specific REF ID with a truck-finding row is selected — never in All Rates view.
    const showSettings = selectedPostId != null && !!selectedPosting?.truckFindingAi;

    return (
        <div className="rounded-lg border border-utilityGray200 shadow-[0_0_10px_3px_rgba(16,24,40,0.04)] sticky bottom-0">
            <div className="flex gap-x-2 items-center justify-between cursor-pointer px-3 py-2.5" onClick={handleToggle}>
                <div className="flex items-center gap-x-1 flex-1 min-w-0">
                    {/* Animated agent "thinking" dots — same indicator as AiThinking; marks this as the rate-gathering agent's card. */}
                    <span className="agent-thinking-animation-wrap flex-none">
                        <span className="agent-thinking-animation-dots">
                            <span className="agent-thinking-animation-dot" aria-hidden="true"></span>
                            <span className="agent-thinking-animation-dot" aria-hidden="true"></span>
                            <span className="agent-thinking-animation-dot" aria-hidden="true"></span>
                            <span className="agent-thinking-animation-dot" aria-hidden="true"></span>
                        </span>
                    </span>
                    {refIdOptions.length > 0 && (
                        <div className="agent-thinking-animation" onClick={(e) => e.stopPropagation()}>
                            <SelectBox
                                size="xs"
                                name="refId"
                                id="refId"
                                parentClassName="min-w-[85px] selectbox-without-border-icon"
                                options={refIdOptions}
                                value={refIdValue}
                                onChangeFunc={(opt: any) => {
                                    if (!onSelectPostId) return;
                                    onSelectPostId(opt?.value === 'all' ? null : Number(opt.value));
                                }}
                                isSearchable={false}
                            />
                        </div>
                    )}
                </div>
                {showSettings && (
                    <OutsideClickHandler onOutsideClick={() => setIsSettingsOpen(false)} containerClassName="relative shrink-0">
                        <div ref={settingsTriggerRef} className="flex items-center gap-x-1" onClick={(e) => e.stopPropagation()}>
                            <TooltipCmp message={[targetRates != null && targetRates > 0 ? 'Rates collected / target' : null, callsEndAt ? 'Call time left' : null].filter(Boolean).join(' • ')}>
                                <div className="flex items-center gap-x-1">
                                    {targetRates != null && targetRates > 0 && (
                                        <span className="text-textSecondary text-[12px] leading-[18px] font-medium cursor-pointer" onClick={handleSettingsToggle}>
                                            {completedRates}/{targetRates}
                                        </span>
                                    )}
                                    {callsEndAt && (
                                        <>
                                            {targetRates != null && targetRates > 0 && <div className="w-px h-3 bg-borderPrimary" />}
                                            <div className="flex items-center gap-x-1 text-primary text-[12px] leading-[18px] font-medium tabular-nums cursor-pointer" onClick={handleSettingsToggle}>
                                                <LiveTimer endDate={callsEndAt} />
                                                <Clock className="w-3.5 h-3.5 text-primary flex-none" />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </TooltipCmp>
                        </div>
                        {isSettingsOpen && (
                            <div
                                className={`absolute right-0 z-[100] w-[185px] bg-white rounded-lg border border-utilityGray200 shadow-lg p-3 ${
                                    settingsDropPosition === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
                                }`}
                                // The dropdown lives inside the header row whose onClick collapses the
                                // rate list — keep form clicks from bubbling up to that toggle.
                                onClick={(e) => e.stopPropagation()}
                            >
                                <InputText
                                    inputName="targetRates"
                                    label="Target Rates"
                                    labelClassName="block mb-1.5"
                                    placeholder="Target Rates"
                                    value={targetRatesInput}
                                    maxLength={4}
                                    required
                                    isError={isTargetRatesError}
                                    onChangeFunc={(e) => {
                                        const val = e.target.value;

                                        if (val !== '0' && /^\d*$/.test(val)) {
                                            setTargetRatesInput(val);
                                            if (val) setIsTargetRatesError(false);
                                        }
                                    }}
                                />
                                <InputText
                                    parentClassName="mt-2"
                                    inputName="acceptCallsFor"
                                    label="Accept Calls For"
                                    labelClassName="mb-1.5"
                                    tooltipMessage={elapsedMinutes > 0 ? `${elapsedMinutes} min have already passed — enter a value greater than ${elapsedMinutes} min.` : undefined}
                                    placeholder="Enter time of collect rates"
                                    value={acceptCallsForInput}
                                    maxLength={4}
                                    required
                                    isError={isAcceptCallsForError}
                                    errorText={isAcceptCallsForError && elapsedMinutes > 0 ? `Must be greater than ${elapsedMinutes} min (time already elapsed).` : undefined}
                                    onFocus={() => {
                                        setAcceptCallsForInput((prev) => prev.replace(/min/g, '').trim());
                                    }}
                                    onChangeFunc={(e) => {
                                        const val = e.target.value;

                                        if (val !== '0' && /^\d*$/.test(val)) {
                                            setAcceptCallsForInput(val);
                                            if (val) setIsAcceptCallsForError(false);
                                        }
                                    }}
                                    onBlur={(e: any) => {
                                        const val = e.target.value.trim();
                                        setAcceptCallsForInput(val ? `${parseInt(val)} min` : '');
                                    }}
                                />
                                <div className="flex justify-end mt-2">
                                    <ButtonCmp className="btn-link-outline-primary !text-xs !font-semibold !p-0 !h-auto !min-h-0" onClick={handleSettingsSave}>
                                        Save
                                    </ButtonCmp>
                                </div>
                            </div>
                        )}
                    </OutsideClickHandler>
                )}
                <ChevronUp className={`w-4 h-4 text-textSecondary transition-transform duration-300 shrink-0 ${isOpen ? '' : 'rotate-180'}`} />
            </div>
            <div
                style={{
                    display: 'grid',
                    gridTemplateRows: isOpen ? '1fr' : '0fr',
                    transition: 'grid-template-rows 0.3s ease',
                }}
            >
                <div className="overflow-hidden">
                    <div className="pb-2.5 mx-3">
                        {items.length === 0 ? (
                            <div className="rounded-lg border border-utilityGray200 bg-gray50 p-3 text-center">
                                <p className="text-xs text-textSecondary font-normal leading-[1.5]">No rates collected yet.</p>
                            </div>
                        ) : (
                            <ul className="rounded-lg border border-utilityGray200 bg-gray50 p-1 space-y-1 max-h-[215px] overflow-y-auto custom-scrollbar scrollbar-hide">
                                {items.map((item, index) => (
                                    <li
                                        key={index}
                                        onClick={item.onItemClick}
                                        className={`agent-message-stepper-row-text overflow-hidden p-1 rounded-[inherit] ${item.onItemClick ? 'cursor-pointer hover:bg-gray200 ' : ''}`}
                                    >
                                        {/* Reserve the verified-tick column even when the tick is absent so every
                                            label lines up — otherwise a non-verified row (e.g. an in-progress lead)
                                            sits flush-left while verified carriers are indented by the tick width. */}
                                        {item.icon === 'verified' ? (
                                            <figure className="shrink-0">
                                                <img src={verifiedIcon} className="w-3 h-3" />
                                            </figure>
                                        ) : (
                                            <figure className="shrink-0">
                                                <img src={verifiedGrayIcon} className="w-3 h-3" />
                                            </figure>
                                        )}

                                        <div className={`agent-message-stepper-row-list-content overflow-hidden`}>
                                            <span className="truncate min-w-0">{item.label}</span>

                                            {item.value && <span className={`agent-message-stepper-row-list-value whitespace-nowrap shrink-0`}>{item.value}</span>}
                                        </div>

                                        {/* Right-aligned group: completion check sits on the right next to the action icons */}
                                        {(item.status || item.actionType) && (
                                            <div className="flex items-center gap-x-1 shrink-0">
                                                {item.status === 'completed' ? (
                                                    <img src={CheckCircleDark} alt="check" className="w-3.5 h-3.5" />
                                                ) : item.status === 'loading' ? (
                                                    <Clock className="w-3.5 h-3.5 text-primary" />
                                                ) : null}

                                                {item.actionType === 'inboundCall' ? (
                                                    <PhoneIncoming02 color="#475467" className="w-3.5 h-3.5 text-gray500" />
                                                ) : item.actionType === 'outboundCall' ? (
                                                    <PhoneOutgoing02 color="#475467" className="w-3.5 h-3.5 text-gray500" />
                                                ) : item.actionType === 'outboundEmail' ? (
                                                    <img src={outboundEmailIcon} alt="Outbound Email" className="w-3.5 h-3.5 text-gray500" />
                                                ) : item.actionType === 'inboundEmail' ? (
                                                    <img src={inboundEmailIcon} alt="Inbound Email" className="w-3.5 h-3.5 text-gray500" />
                                                ) : item.actionType === 'manual' ? (
                                                    <User01 color="#475467" className="w-3.5 h-3.5 text-gray500" />
                                                ) : null}
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    {hasRateStats && (
                        <div className="flex items-center justify-between px-3 pb-2.5">
                            <TooltipCmp message={avgRateTooltip} childrenClassName="cursor-default flex">
                                <span className="text-xs">
                                    <span className="text-textSecondary">Avg.</span> <span className="text-grayLight900 font-medium">{getFormattedNumber(avgRate, false, true, true)}</span>
                                </span>
                            </TooltipCmp>
                            <TooltipCmp message={`Lowest rate: ${minRateDisplay}`} childrenClassName="cursor-default flex">
                                <span className="text-xs">
                                    <span className="text-textSecondary">Min.</span> <span className="text-grayLight900 font-medium">{`$${getFormattedNumber(minRate, false, false, true)}`}</span>
                                </span>
                            </TooltipCmp>
                            <TooltipCmp message={`Highest rate: ${maxRateDisplay}`} childrenClassName="cursor-default flex">
                                <span className="text-xs">
                                    <span className="text-textSecondary">Max.</span> <span className="text-grayLight900 font-medium">{`$${getFormattedNumber(maxRate, false, false, true)}`}</span>
                                </span>
                            </TooltipCmp>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AllRatesLists;
